import { ref, onScopeDispose, type Ref } from 'vue'

/**
 * Estado reactivo de conectividad del navegador. Sembrado de `navigator.onLine`
 * y actualizado por los eventos `online` / `offline` de `window`.
 *
 * SSR-safe: si `navigator`/`window` no existen (servidor), devuelve `true`
 * (asumimos conectado) y no registra listeners.
 *
 * Los listeners se limpian automáticamente al destruirse el scope del
 * componente que la usa (`onScopeDispose`). Regla G-245.
 *
 * Limitación conocida: `navigator.onLine` refleja el estado de la interfaz de red
 * del SO, no la alcanzabilidad real de internet. Devuelve `true` en portales
 * cautivos / redes restringidas. Por eso `error.vue` clasifica la conexión también
 * por `isChunkLoadError` (independiente de onLine).
 *
 * AUTO-CURADO (G-245, incidente Dra. Sonia 2026-06-16): el evento `online` solo
 * dispara cuando el navegador detecta la transición offline→online. En la práctica
 * esa bandera se TRABA en `false` (bug conocido tras dormir/despertar el equipo, VPN,
 * wifi cautivo) y el evento `online` jamás dispara → la barra "sin conexión" se queda
 * pegada para siempre hasta recargar. Para no depender de un evento poco fiable,
 * mientras estamos offline sondeamos activamente la alcanzabilidad real (HEAD a un
 * asset propio, mismo origen, sin caché). Apenas un sondeo responde, volvemos a
 * `online = true` aunque el evento nunca llegue. Ruta normal (online) = cero sondeos.
 */

const PROBE_INTERVAL_MS = 5000
const PROBE_TIMEOUT_MS = 5000

export function useOnlineStatus(): { online: Ref<boolean> } {
  const isClient = typeof window !== 'undefined' && typeof navigator !== 'undefined'
  const online = ref<boolean>(isClient ? navigator.onLine : true)

  if (!isClient) return { online }

  let pollId: ReturnType<typeof setInterval> | null = null
  let inFlight: AbortController | null = null
  let probing = false

  /** Sondeo de alcanzabilidad real. Resuelve `true` si el servidor responde
   *  (cualquier status, incluso 404 = la red funciona), `false` si la red falla
   *  o se agota el tiempo. `no-cors` + `no-store` evita CORS y caché. */
  async function reachable(): Promise<boolean> {
    if (typeof fetch === 'undefined') return false
    const ctrl = new AbortController()
    inFlight = ctrl
    const timer = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS)
    try {
      await fetch(`${window.location.origin}/favicon.ico?_probe=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store',
        mode: 'no-cors',
        signal: ctrl.signal,
      })
      return true
    } catch {
      return false
    } finally {
      clearTimeout(timer)
      if (inFlight === ctrl) inFlight = null
    }
  }

  function stopPoll() {
    if (pollId !== null) { clearInterval(pollId); pollId = null }
    inFlight?.abort()
    inFlight = null
  }

  function startPoll() {
    if (pollId !== null) return
    pollId = setInterval(async () => {
      if (probing) return
      probing = true
      try {
        if (await reachable()) { online.value = true; stopPoll() }
      } finally {
        probing = false
      }
    }, PROBE_INTERVAL_MS)
  }

  const goOnline  = () => { online.value = true; stopPoll() }
  const goOffline = () => { online.value = false; startPoll() }

  window.addEventListener('online', goOnline)
  window.addEventListener('offline', goOffline)

  // Sembrado offline (incl. navigator.onLine trabado en false): arrancar el
  // auto-curado de inmediato para no quedar pegados si el evento nunca dispara.
  if (!online.value) startPoll()

  onScopeDispose(() => {
    window.removeEventListener('online', goOnline)
    window.removeEventListener('offline', goOffline)
    stopPoll()
  })

  return { online }
}
